import { clsx } from 'clsx'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { type SuggestStore } from './suggest'

/** The open list, under the typed text. */
export function SuggestList({ store }: { store: SuggestStore }) {
  const s = useSyncExternalStore(store.subscribe, store.getSnapshot)
  const list = useRef<HTMLUListElement>(null)
  const active = s?.active ?? 0
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])
  if (!s?.rect) return null
  const below = s.rect.bottom + 6
  const room = window.innerHeight - below
  const style =
    room < 220
      ? { left: s.rect.left, bottom: window.innerHeight - s.rect.top + 6 }
      : { left: s.rect.left, top: below }
  return createPortal(
    <div
      data-suggest-list=""
      className="fixed z-50 w-72 overflow-hidden rounded-lg border border-line bg-surface text-[13px] shadow-lg"
      style={style}
      // Clicking the list must not take focus (and the selection) from the editor.
      onMouseDown={(e) => {
        e.preventDefault()
      }}
    >
      {s.items.length === 0 ? (
        <div className="px-3 py-2 text-muted">{s.empty}</div>
      ) : (
        <ul
          ref={list}
          role="listbox"
          aria-label={s.label}
          className="max-h-72 overflow-y-auto py-1"
        >
          {s.items.map((item, i) => {
            const Icon = item.icon
            return (
              <li
                key={item.key}
                role="option"
                aria-selected={i === s.active}
                onPointerEnter={() => {
                  if (i !== s.active) store.set({ ...s, active: i })
                }}
                onClick={() => {
                  s.pick(item)
                }}
                className={clsx(
                  'flex cursor-pointer items-start gap-2.5 px-3 py-1.5',
                  i === s.active && 'bg-surface-2',
                )}
              >
                <Icon size={14} className="mt-0.5 flex-none text-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{item.label}</span>
                  {item.detail && (
                    <span className="block truncate text-[12px] text-muted">{item.detail}</span>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>,
    document.body,
  )
}
